import type z from 'zod/v4'
import { index, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { user } from '../auth'

// =============================================================================
// METRIC DIRTY DAY - ledger of past (user, UTC day) pairs the nightly roll-up must recompute
// =============================================================================
//
// The nightly `cron / rollup-lookup-tables` recomputes every child who had tracker activity in the
// last three UTC days, so ordinary writes never need to mark anything. This ledger covers the rest:
// an edit that moves a figure onto a day OLDER than that window (a re-grade of last month's work, a
// bulk scoreboard edit with an explicit past `endedAt`, an enrollment expiring into FAILED).
//
// Written by `markMetricDaysDirty` (packages/trigger/src/metrics/dirty-days.ts), which floors to the
// UTC day and never marks the current day (today is always computed live). Read and cleared only by
// the roll-up, by id, after the chunk that recomputed the day has committed — so a crash mid-run
// leaves the mark in place and the next run redoes it. Recomputing a clean day is harmless.

export const MetricDirtyDay = pgTable(
  'metric_dirty_day',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // start-of-day UTC, like every lookup table's `date`
    reason: text(), // free-form marker for debugging: 'regrade' | 'bulk-edit' | 'expire' | …
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    unique('uq_metric_dirty_day_user_date').on(table.userId, table.date),
    index('idx_metric_dirty_day_created_at').on(table.createdAt),
  ],
)

export const MetricDirtyDaySchema = createSelectSchema(MetricDirtyDay)
export const InsertMetricDirtyDaySchema = createInsertSchema(MetricDirtyDay)

export type MetricDirtyDayType = z.infer<typeof MetricDirtyDaySchema>
export type InsertMetricDirtyDayType = z.infer<typeof InsertMetricDirtyDaySchema>
